import React, { useMemo } from 'react'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '../../firebase'
import { useError, useForm, useNotifications } from '../../hooks'
import CheckboxField from '../Form/CheckboxField'
import Button from '../Button'
import { USER_TYPE_FLAGS } from '../CreateUserType'
import { iconClassName } from '../Dispatch/DispatchMap/icons'
import {
  sendNewIncidentNotification,
  sendNotificationToTopic
} from '../../hooks/notificationService'

const ORGS_PREFIX = 'organization:'

const DISPATCH_FLAGS = {
  INCLUDE_OFFDUTY: 1 << 0
}

export default function AddDispatchOrganization({
  incident,
  dispatchBindings,
  onDispatchOrganizationCreated,
  userTypes,
  profile
}) {
  const setError = useError()
  const addNotification = useNotifications()
  const [getFieldProps, formData, formError] = useForm()

  const existingDispatchOrgs = useMemo(
    () =>
      incident.dispatchOrganizations.reduce(
        (acc, { id, name, dispatchFlags, offDuty }) => ({
          ...acc,
          [id]: {
            name,
            offDuty:
              offDuty || (dispatchFlags & DISPATCH_FLAGS.INCLUDE_OFFDUTY) !== 0
          }
        }),
        {}
      ),
    [incident]
  )

  const userTypesById = useMemo(() => {
    return userTypes.reduce(
      (acc, userType) => ({
        ...acc,
        [userType.id]: userType
      }),
      {}
    )
  }, [userTypes])

  const dispatchOrgs = useMemo(() => {
    if (!userTypes?.length || !profile?.organization) return {}

    const dispatchableUserTypes = userTypes.filter(
      ({ flags }) => !!(flags & USER_TYPE_FLAGS.DISPATCHABLE)
    )

    // ✅ Use a Map to prevent duplicates (own org + dispatch orgs)
    const orgSet = new Map()

    // Always include the user's own organization
    orgSet.set(profile.organization.id, profile.organization)

    // Add dispatch binding organizations
    if (dispatchBindings?.length) {
      for (const { dispatchOrganization } of dispatchBindings) {
        if (dispatchOrganization) {
          orgSet.set(dispatchOrganization.id, dispatchOrganization)
        }
      }
    }

    // Convert to array
    const orgsArray = Array.from(orgSet.values())

    // ✅ Group by user type (while keeping your org even if not dispatchable)
    return orgsArray.reduce((acc, organization) => {
      const userTypeId =
        organization.defaultUserType?.id ||
        profile.userType?.id || // fallback if org has no default user type
        'unknown'

      const isDispatchable = dispatchableUserTypes.some(
        (ut) => ut.id == userTypeId
      )
      const isCurrentOrg = organization.id === profile.organization.id

      // ✅ Keep both dispatchable + current orgs
      if (isDispatchable || isCurrentOrg) {
        if (!acc[userTypeId]) acc[userTypeId] = []
        acc[userTypeId].push(organization)
      }

      return acc
    }, {})
  }, [userTypes, dispatchBindings, profile])

  async function handleSubmit(e) {
    e.preventDefault()

    const dispatchOrganizationIds = Object.entries(formData)
      .filter(([key, value]) => key.startsWith(ORGS_PREFIX) && value)
      .map(([key]) => key.slice(ORGS_PREFIX.length))

    if (dispatchOrganizationIds.length === 0) {
      setError({ dispatchOrganizations: true })
      return
    }

    try {
      const incidentRef = doc(db, 'incidents', incident.id)
      const currentOrgs = [...(incident.dispatchOrganizations || [])]

      const updatedOrgs = currentOrgs.map((org) => {
        const orgId = org.id || org.organizationId

        if (dispatchOrganizationIds.includes(orgId)) {
          const offDutyKey = `offduty:${ORGS_PREFIX}${orgId}`
          const hasOffDuty = Object.prototype.hasOwnProperty.call(
            formData,
            offDutyKey
          )
          const newOffDuty = hasOffDuty ? formData[offDutyKey] : org.offDuty

          return {
            ...org,
            offDuty: newOffDuty,
            dispatchFlags: newOffDuty
              ? (org.dispatchFlags || 0) | DISPATCH_FLAGS.INCLUDE_OFFDUTY
              : (org.dispatchFlags || 0) & ~DISPATCH_FLAGS.INCLUDE_OFFDUTY
          }
        }
        return org
      })

      const newOrgs = dispatchOrganizationIds
        .filter(
          (orgId) =>
            !currentOrgs.some((org) => (org.id || org.organizationId) === orgId)
        )
        .map((orgId) => {
          // Find the organization in any of the dispatchOrgs groups - same method as CreateIncidentPanel
          let orgData = null
          for (const orgs of Object.values(dispatchOrgs)) {
            orgData = orgs.find((org) => org.id === orgId)
            if (orgData) break
          }

          return {
            id: orgId,
            name: orgData?.name || `Organization ${orgId}`,
            offDuty: formData[`offduty:${ORGS_PREFIX}${orgId}`] || false,
            dispatchFlags: formData[`offduty:${ORGS_PREFIX}${orgId}`]
              ? DISPATCH_FLAGS.INCLUDE_OFFDUTY
              : 0,
            createdAt: orgData?.createdAt || new Date().toISOString()
          }
        })

      const finalOrgs = [...updatedOrgs, ...newOrgs]
      const allOrgIds = [...new Set(finalOrgs.map((org) => org.id))]
      await updateDoc(incidentRef, {
        dispatchOrganizations: finalOrgs,
        dispatchOrganizationsIdList: allOrgIds
      })

      addNotification(
        <div>
          <div>Responders updated successfully.</div>
        </div>,
        { className: 'is-success' }
      )

      const newlyAddedOrgs = newOrgs.map((org) => ({
        id: org.id,
        name: org.name,
        offDuty: org.offDuty
      }))

      for (const org of newlyAddedOrgs) {
        try {
          await sendNewIncidentNotification(incident, org, org.offDuty)
        } catch (notificationError) {
          console.error(
            `Failed to send notification for org ${org.id}:`,
            notificationError
          )
          // Don't fail the operation if notifications fail
          addNotification(
            <div>
              <div>Notification failed for {org.name}</div>
              <div>
                <small>
                  Organization was added but notification failed to send
                </small>
              </div>
            </div>,
            { className: 'is-warning', autoDismiss: true }
          )
        }
      }

      // Send incident update notification to the incident topic
      try {
        const incidentUpdateNotification = {
          topic: `${
            process.env.REACT_APP_ENV === 'production' ? 'p' : ''
          }livealarm-incident-${incident.id}`,
          title: 'Incident Updated',
          body: `New responders added to incident: ${incident.name}`,
          data: {
            type: 'incident_update',
            incidentId: incident.id
          }
        }

        await sendNotificationToTopic(incidentUpdateNotification)
      } catch (updateError) {
        console.error(
          `Failed to send incident update notification:`,
          updateError
        )
      }

      onDispatchOrganizationCreated && onDispatchOrganizationCreated()
    } catch (err) {
      console.error('Error updating dispatch organizations:', err)
      if (err.message === 'validation_error') {
        formError(err.props)
      } else {
        setError(err)
      }
    }
  }

  return (
    <div style={{ width: '100%', display: 'flex', padding: '1rem' }}>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        {Object.entries(dispatchOrgs).map(([userTypeId, organizations]) => (
          <div className='dispatch-type-select-item' key={userTypeId}>
            <div className='dispatch-type-select-item-header'>
              <div className='dispatch-type-select-item-header-icon'>
                <i
                  className={iconClassName(userTypesById[userTypeId].icon)}
                  style={{ color: userTypesById[userTypeId].color }}
                />
              </div>
              <span>{userTypesById[userTypeId].name}</span>
            </div>
            <div className='dispatch-type-select-item-body'>
              {organizations.map(({ id, name }) => (
                <div key={id}>
                  <CheckboxField
                    name={`${ORGS_PREFIX}${id}`}
                    label={name}
                    defaultChecked={!!existingDispatchOrgs[id]}
                    disabled={!!existingDispatchOrgs[id]}
                    {...getFieldProps()}
                  />
                  {(formData && formData[`${ORGS_PREFIX}${id}`]) ||
                  existingDispatchOrgs[id] ? (
                    <CheckboxField
                      name={`offduty:${ORGS_PREFIX}${id}`}
                      label='Include Off-Duty'
                      disabled={!!existingDispatchOrgs[id]?.offDuty}
                      defaultChecked={
                        existingDispatchOrgs[id]?.offDuty ||
                        (formData && formData[`offduty:${ORGS_PREFIX}${id}`])
                      }
                      {...getFieldProps()}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className='level'>
          <div className='level-left' />
          <div className='level-right'>
            <div className='level-item'>
              <Button type='submit'>Add responders</Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
